import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Modal from '@mui/material/Modal';
import CircularProgress from '@mui/material/CircularProgress';
import QRCode from 'qrcode';
import * as loyaltyActions from '../../../LoyaltyPage/actions';
import { selectors as loyaltySelectors } from '../../../LoyaltyPage/reducer';
import * as Styled from './IssuingWithoutBasket.styles';
import AvailableReceiptsBlock from '../../../LoyaltyPage/components/AvailableReceiptsBlock';

type ReceiptItem = {
  ware_id: string;
  name: string;
  quantity: number;
  unit_price: number;
  appliedBonus: number;
  part_quantity: number;
};

type OrderItem = {
  were_id: string;
  name: string;
  quantity: number;
};

type ReceiptCheckRow = {
  wareId: string;
  receiptItem: ReceiptItem | undefined;
  orderItem: OrderItem | undefined;
  isOk: boolean;
  errorText: string;
};

const IssuingWithoutBasket: React.FC<{
  products: any;
  paymentType: string;
  orderNumber: string;
  setIsShowClosingStandartText: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ products, paymentType, setIsShowClosingStandartText, orderNumber }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const currentReceipt = useSelector(loyaltySelectors.currentReceipt);
  const currentReceiptLoading = useSelector(
    loyaltySelectors.currentReceiptLoading
  );
  const currentReceiptError = useSelector(loyaltySelectors.currentReceiptError);
  const chosenReceipt = useSelector(loyaltySelectors.chosenReceipt);
  const sendToStandartSuccess = useSelector(
    loyaltySelectors.sendToStandartSuccess
  );
  const sendToStandartError = useSelector(loyaltySelectors.sendToStandartError);

  const [clicked, setClicked] = useState<boolean>(false);

  const [qrSrc, setQrSrc] = useState<string>('');
  const [qrError, setQrError] = useState<string>('');
  const [isQrModalOpen, setIsQrModalOpen] = useState<boolean>(false);

  const getReceiptInfo = useCallback(() => {
    !currentReceiptLoading &&
      dispatch(loyaltyActions.getCurrentReceipt.request());
  }, [currentReceiptLoading]);

  const basketStatus = useMemo(() => {
    if (currentReceiptError?.message) {
      return {
        text: t(`LoyaltyPage.${currentReceiptError?.message}`),
        status: 'error',
      };
    }
    if (!currentReceipt?.length) {
      return {
        text: t('OrderPage.IssuingWithoutBasket.StatusCases.NO_RECEIPTS'),
        status: 'error',
      };
    }
    if (!chosenReceipt) {
      return {
        text: t('OrderPage.IssuingWithoutBasket.StatusCases.CHOOSE_RECEIPT'),
        status: 'warning',
      };
    }
    if (chosenReceipt) {
      const chosenMap = new Map(
        chosenReceipt?.items?.map((item) => [item.ware_id, item.quantity])
      );

      const dataMap = new Map(
        products.map((item: { were_id: any; quantity: any }) => [
          item.were_id,
          item.quantity,
        ])
      );

      const missingInChosen = products.filter(
        (item: { were_id: string }) => !chosenMap.has(item.were_id)
      );

      const extraInChosen = chosenReceipt?.items?.filter(
        (item) => !dataMap.has(item.ware_id)
      );

      if (missingInChosen.length) {
        return {
          text: t(
            'OrderPage.IssuingWithoutBasket.StatusCases.NO_NECESSARY_PRODUCTS'
          ),
          status: 'warning',
        };
      }

      if (extraInChosen.length) {
        return {
          text: t('OrderPage.IssuingWithoutBasket.StatusCases.EXTRA_PRODUCTS'),
          status: 'warning',
        };
      }

      const quantityMismatch = products.some(
        (item: { were_id: string; quantity: number | undefined }) =>
          chosenMap.get(item.were_id) !== item.quantity
      );

      if (quantityMismatch) {
        return {
          text: t('OrderPage.IssuingWithoutBasket.StatusCases.COUNT_DISMATCH'),
          status: 'warning',
        };
      }

      return {
        text: t('OrderPage.IssuingWithoutBasket.StatusCases.RECEIPT_MATCHES'),
        status: 'success',
      };
    }
    return {
      text: t('OrderPage.IssuingWithoutBasket.StatusCases.CHOOSE_RECEIPT'),
      status: 'success',
    };
  }, [currentReceipt, currentReceiptError, chosenReceipt]);

  useEffect(() => {
    if (!clicked) {
      return;
    }

    getReceiptInfo();

    const intervalId = setInterval(() => {
      getReceiptInfo();
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [clicked]);

  useEffect(() => {
    return () => {
      dispatch(loyaltyActions.resetCurrentReceipt());
      setClicked(false);
      dispatch(loyaltyActions.setChosenReceipt.request(null));
    };
  }, []);

  useEffect(() => {
    if (basketStatus.status === 'success' && chosenReceipt) {
      // eslint-disable-next-line
      const payment_type = paymentType === 'in_place' ? 'in_place' : 'internet';
      dispatch(
        loyaltyActions.sendToStandart.request({
          // eslint-disable-next-line
          payment_type,
          order_number: orderNumber,
          doc_id: chosenReceipt?.doc_id,
          items: chosenReceipt?.items.map((item) => ({
            ware_id: item.ware_id,
            price: products?.find(
              (product: { were_id: string }) => product.were_id === item.ware_id
            )?.price,
            quantity: item.part_quantity,
          })),
          postEffect: async () => {
            try {
              setQrError('');
              const payload = JSON.stringify({
                // eslint-disable-next-line
                payment_type,
                order_number: orderNumber,
                doc_id: chosenReceipt?.doc_id,
                items: chosenReceipt?.items.map((item) => ({
                  ware_id: item.ware_id,
                  price: products?.find(
                    (product: { were_id: string }) =>
                      product.were_id === item.ware_id
                  )?.price,
                  quantity: item.part_quantity,
                })),
              });
              const dataUrl = await QRCode.toDataURL(payload, {
                errorCorrectionLevel: 'M',
                margin: 1,
                scale: 4,
              });
              setQrSrc(dataUrl);
            } catch (e: any) {
              console.error('QR generation error:', e);
              setQrSrc('');
              setQrError(e?.message || t('LoyaltySeparated.QR_ERROR'));
            }
          },
        })
      );
    }
  }, [basketStatus.status]);

  const maxLength = useMemo(
    () => Math.max(chosenReceipt?.items?.length || 0, products?.length || 0),
    [chosenReceipt, products]
  );

  const receiptCheckRows = useMemo((): ReceiptCheckRow[] => {
    if (!chosenReceipt) return [];

    const chosenMap = new Map<string, ReceiptItem>(
      chosenReceipt?.items?.map((item: ReceiptItem) => [item.ware_id, item])
    );
    const dataMap = new Map<string, OrderItem>(
      products.map((item: OrderItem) => [item.were_id, item])
    );

    const allWareIds = new Set<string>([
      ...(chosenReceipt?.items ?? []).map((item: ReceiptItem) => item.ware_id),
      ...products.map((item: OrderItem) => item.were_id),
    ]);

    return Array.from(allWareIds).map((wareId) => {
      const receiptItem = chosenMap.get(wareId);
      const orderItem = dataMap.get(wareId);

      let isOk = false;
      let errorText = '';

      if (!orderItem) {
        errorText = t(
          'OrderPage.IssuingWithoutBasket.ProductsTable.REMOVE_PRODUCT'
        );
      } else if (!receiptItem) {
        errorText = t(
          'OrderPage.IssuingWithoutBasket.ProductsTable.ADD_PRODUCT'
        );
      } else if (receiptItem.quantity !== orderItem.quantity) {
        errorText = t(
          'OrderPage.IssuingWithoutBasket.ProductsTable.WRONG_QUANTITY',
          { quantity: orderItem.quantity }
        );
      } else {
        isOk = true;
      }

      return { wareId, receiptItem, orderItem, isOk, errorText };
    });
  }, [chosenReceipt, products]);

  return (
    <Styled.IssuingWithoutBasketContentContainer>
      <Styled.IssuingWithoutBasketContainer>
        <Styled.IssuingWithoutBasketButton
          clicked={clicked}
          onClick={() => {
            setClicked(!clicked);
            setIsShowClosingStandartText(clicked);
          }}
        >
          {(currentReceiptLoading || currentReceiptError?.message) &&
          !currentReceipt ? (
            <CircularProgress size={20} />
          ) : (
            <div>{t('OrderPage.IssuingWithoutBasket.ISSUING_BTN')}</div>
          )}
        </Styled.IssuingWithoutBasketButton>
        {(currentReceipt || currentReceiptError) && (
          <Styled.IssuingWithoutBasketStatus
            status={basketStatus.status}
            onClick={() => setClicked(!clicked)}
          >
            {basketStatus.text}
          </Styled.IssuingWithoutBasketStatus>
        )}

        {(sendToStandartSuccess || sendToStandartError) && (
          <Styled.StandartStatus error={!!sendToStandartError}>
            {sendToStandartError ? '✘' : '✓'}{' '}
            {t(
              `LoyaltySeparated.${
                sendToStandartError ? 'ISSUING_ERROR' : 'ORDER_SUCCESS'
              }`,
              {
                title: 'заказ',
              }
            )}
            {sendToStandartError && t(`LoyaltySeparated.PLAESE_SCAN_QR`)}
          </Styled.StandartStatus>
        )}
        {qrSrc && sendToStandartError && (
          <Styled.QrContainer>
            <Styled.BlockHint style={{ margin: 0, textAlign: 'center' }}>
              {t('LoyaltySeparated.QR_HINT_ISSUING')}
            </Styled.BlockHint>
            <Styled.QrPreviewButton
              type="button"
              onClick={() => setIsQrModalOpen(true)}
            >
              <img alt="QR" width={180} height={180} src={qrSrc} />
            </Styled.QrPreviewButton>
          </Styled.QrContainer>
        )}
        {/* увеличенный QR */}
        <Modal open={isQrModalOpen} onClose={() => setIsQrModalOpen(false)}>
          <Styled.QrModalContent>
            {qrSrc ? <Styled.QrBig alt="QR big" src={qrSrc} /> : null}
          </Styled.QrModalContent>
        </Modal>
      </Styled.IssuingWithoutBasketContainer>
      {basketStatus.status === 'warning' && (
        <div>
          {chosenReceipt ? (
            <Styled.IssuingWithoutBasketTable>
              <Styled.IssuingWithoutBasketTableRow>
                <Styled.IssuingWithoutBasketTableTd style={{ width: '40%' }}>
                  <p className="header-text">
                    {t(
                      'OrderPage.IssuingWithoutBasket.ProductsTable.TableHeader.RECEIPT_PRODUCT'
                    )}
                  </p>
                </Styled.IssuingWithoutBasketTableTd>
                <Styled.IssuingWithoutBasketTableTd
                  style={{ width: '15%', justifyContent: 'center' }}
                >
                  <p className="header-text">
                    {t(
                      'OrderPage.IssuingWithoutBasket.ProductsTable.TableHeader.ORDER_COUNT'
                    )}
                  </p>
                </Styled.IssuingWithoutBasketTableTd>
                <Styled.IssuingWithoutBasketTableTd
                  style={{ width: '15%', justifyContent: 'center' }}
                >
                  <p className="header-text">
                    {t(
                      'OrderPage.IssuingWithoutBasket.ProductsTable.TableHeader.RECEIPT_COUNT'
                    )}
                  </p>
                </Styled.IssuingWithoutBasketTableTd>
                <Styled.IssuingWithoutBasketTableTd
                  style={{ width: '10%', justifyContent: 'center' }}
                >
                  <p className="header-text">
                    {t(
                      'OrderPage.IssuingWithoutBasket.ProductsTable.TableHeader.CHECK'
                    )}
                  </p>
                </Styled.IssuingWithoutBasketTableTd>
                <Styled.IssuingWithoutBasketTableTd style={{ width: '20%' }}>
                  <p className="header-text">
                    {t(
                      'OrderPage.IssuingWithoutBasket.ProductsTable.TableHeader.ERROR'
                    )}
                  </p>
                </Styled.IssuingWithoutBasketTableTd>
              </Styled.IssuingWithoutBasketTableRow>

              {receiptCheckRows.map(
                ({ wareId, receiptItem, orderItem, isOk, errorText }) => (
                  <Styled.IssuingWithoutBasketTableRow key={wareId}>
                    <Styled.IssuingWithoutBasketTableTd
                      style={{ width: '40%' }}
                    >
                      <p>{receiptItem?.name || orderItem?.name || ''}</p>
                    </Styled.IssuingWithoutBasketTableTd>
                    <Styled.IssuingWithoutBasketTableTd
                      style={{ width: '15%', justifyContent: 'center' }}
                    >
                      <p>{orderItem?.quantity ?? '—'}</p>
                    </Styled.IssuingWithoutBasketTableTd>
                    <Styled.IssuingWithoutBasketTableTd
                      style={{ width: '15%', justifyContent: 'center' }}
                    >
                      <p>{receiptItem?.quantity ?? '—'}</p>
                    </Styled.IssuingWithoutBasketTableTd>
                    <Styled.IssuingWithoutBasketTableTd
                      style={{ width: '10%', justifyContent: 'center' }}
                    >
                      <p>{isOk ? '✅' : '❌'}</p>
                    </Styled.IssuingWithoutBasketTableTd>
                    <Styled.IssuingWithoutBasketTableTd
                      style={{ width: '20%' }}
                    >
                      <p style={{ color: isOk ? 'inherit' : '#ef4444' }}>
                        {errorText}
                      </p>
                    </Styled.IssuingWithoutBasketTableTd>
                  </Styled.IssuingWithoutBasketTableRow>
                )
              )}
            </Styled.IssuingWithoutBasketTable>
          ) : (
            <AvailableReceiptsBlock />
          )}
        </div>
      )}
    </Styled.IssuingWithoutBasketContentContainer>
  );
};

export default IssuingWithoutBasket;
